import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PeriodPills } from './PeriodPills';
import { HorizontalCardCarousel } from './HorizontalCardCarousel';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { RetryImage } from '@/components/ui/RetryImage';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { DashboardPostPopover } from './DashboardPostPopover';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useMappedQueue, MappedQueueItem } from '@/hooks/useDashboardStats';
import { useTimezone } from '@/contexts/TimezoneContext';
import { PostCreator } from '@/components/PostCreator';
import { VariationCreator } from '@/components/VariationCreator';

/**
 * Mapped Section - Posts mapped to calendar awaiting commit (Carousel layout)
 *
 * Features:
 * - Horizontal carousel with arrow navigation
 * - Period filtering (today/week/month)
 * - Real-time status indicators (READY/DRAFT)
 * - Caption-based display
 * - HoverCard popover matching CalendarPostPopover style
 * - Timezone-aware planned dates
 * - Calendar name badges
 *
 * Note: Channels are NOT displayed here since they are assigned during scheduling
 */

type Period = 'today' | 'week' | 'month';

// Status indicator colors for card dots
const statusColors: Record<string, string> = {
  READY: 'bg-teal-400',
  DRAFT: 'bg-gray-400',
};

// Status mapping for HoverCard gradient headers
// `labelKey`/`labelDefault` are resolved via t() inside PostCard (Task #182 i18n fix) —
// reuses the existing `common:status_*` keys rather than hardcoded English.
const STATUS_MAP: Record<string, { gradient: string; icon: string; labelKey: string; labelDefault: string }> = {
  READY: { gradient: 'from-secondary-600 to-secondary-700', icon: ICONS.ROCKET, labelKey: 'common:status_ready', labelDefault: 'Ready' },
  DRAFT: { gradient: 'from-secondary-600 to-secondary-700', icon: ICONS.EDIT, labelKey: 'common:status_draft', labelDefault: 'Draft' },
};

/**
 * Skeleton Card Component for Loading State
 */
const SkeletonCard = () => (
  <div className="flex-none w-[160px] aspect-square bg-gray-200 rounded-xl animate-pulse" />
);

/**
 * Post Card Component
 */
interface PostCardProps {
  item: MappedQueueItem;
  onCommit: (item: MappedQueueItem) => void;
}

const PostCard = ({ item, onCommit }: PostCardProps) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { formatRelativeDate } = useTimezone();

  const handleClick = () => {
    onCommit(item);
  };

  const statusDotColor = statusColors[item.contentStatus] || 'bg-gray-400';
  const statusInfo = STATUS_MAP[item.contentStatus] || {
    gradient: 'from-gray-400 to-gray-500',
    icon: ICONS.FILE,
    // NEW key — not yet in locale JSON, reported to locale-owner (Task #182)
    labelKey: 'common:status_unknown',
    labelDefault: 'Unknown',
  };

  // HoverCard popover content (matching CalendarPostPopover style exactly)
  const popoverContent = (
    <DashboardPostPopover
      gradient={statusInfo.gradient}
      headerTone={statusInfo.gradient.includes('secondary-') ? 'secondary' : 'light'}
      headerIcon={statusInfo.icon}
      headerLabel={t(statusInfo.labelKey, { defaultValue: statusInfo.labelDefault })}
      headerLines={[
        { icon: ICONS.CALENDAR, text: item.calendarName },
      ]}
      postMedia={item.postMedia || []}
      caption={item.caption || ''}
    />
  );

  // Card trigger element - Square layout with full-bleed thumbnail
  const cardTrigger = (
    <div
      onClick={handleClick}
      className="flex-none w-[160px] aspect-square rounded-xl overflow-hidden relative bg-gray-100 cursor-pointer hover:shadow-lg hover:scale-[1.03] transition-all duration-200 group/commit"
    >
      {/* Full-bleed thumbnail */}
      {item.thumbnailUrl ? (
        <RetryImage
          src={item.thumbnailUrl}
          alt=""
          className="w-full h-full object-cover"
          fallback={<PlaceholderImage />}
        />
      ) : (
        <PlaceholderImage />
      )}

      {/* Top overlay: Status dot */}
      <div className="absolute top-2 right-2">
        <div className={cn('w-2.5 h-2.5 rounded-full ring-2 ring-white/50', statusDotColor)} />
      </div>

      {/* Bottom gradient overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 pt-8">
        {/* Calendar name badge */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CALENDAR} size="xs" className="text-white" ariaHidden />
          <span className="text-[11px] text-white font-medium drop-shadow-sm">
            {item.calendarName}
          </span>
        </div>
        {/* Date */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CLOCK} size="xs" className="text-white/70" ariaHidden />
          <span className="text-[10px] text-white/80 drop-shadow-sm">
            {formatRelativeDate(item.plannedDate)}
          </span>
        </div>
        {/* Caption */}
        <p className={cn(
          'text-[11px] text-white/90 line-clamp-1 drop-shadow-sm',
          !item.caption && 'italic'
        )}>
          {item.caption ? (item.caption.length > 40 ? `${item.caption.slice(0, 40)}...` : item.caption) : t('common:mapped_no_caption')}
        </p>
      </div>

      {/* Hover overlay - commit action */}
      <div className="absolute inset-0 bg-black/0 group-hover/commit:bg-black/30 transition-all duration-200 flex items-center justify-center rounded-xl">
        <Tooltip content={t('common:commit_to_schedule', { defaultValue: 'Commit & Schedule' })} className="!z-[10001]">
          <div className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center opacity-0 group-hover/commit:opacity-100 transition-opacity duration-200">
            <span className="material-symbols-outlined text-[20px] text-teal-600" aria-hidden="true">rocket_launch</span>
          </div>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <HoverCard
      trigger={cardTrigger}
      side="right"
      align="start"
    >
      {popoverContent}
    </HoverCard>
  );
};

/**
 * Empty State Component
 */
const EmptyState = () => {
  const { t } = useTranslation('dashboard');

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full grid place-items-center mb-4">
        <Icon name={ICONS.CALENDAR} size="xl" color="text-gray-400" ariaHidden />
      </div>
      <p className="text-gray-500 text-sm">
        {t('dashboard_mapped_empty', { defaultValue: 'No mapped posts for this period' })}
      </p>
    </div>
  );
};

/**
 * Main Mapped Section Component
 */
export const MappedSection = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('week');
  const { data, isLoading } = useMappedQueue(period);

  // Commit modal state
  const [isPostCreatorOpen, setIsPostCreatorOpen] = useState(false);
  const [editingPostId, setEditingPostId] = useState<number | undefined>(undefined);
  const [shootingMappingId, setShootingMappingId] = useState<number | undefined>(undefined);
  const [shootingPlannedDate, setShootingPlannedDate] = useState<Date | undefined>(undefined);
  const [isVariationCreatorOpen, setIsVariationCreatorOpen] = useState(false);
  const [shootingVariationMappingId, setShootingVariationMappingId] = useState<number | undefined>(undefined);

  const handleViewAll = () => {
    navigate('/calendar');
  };

  const handleCommit = (item: MappedQueueItem) => {
    if (item.variationId) {
      // Variation mapping - open VariationCreator in shooting mode
      setShootingVariationMappingId(item.id); // item.id = CalendarMapping.id
      setIsVariationCreatorOpen(true);
    } else {
      // Post mapping - open PostCreator in shooting mode
      setEditingPostId(item.postId);
      setShootingMappingId(item.id); // item.id = CalendarMapping.id
      // Seed the composer with the mapping's planned date so there is no "today" flash
      const planned = new Date(item.plannedDate);
      setShootingPlannedDate(Number.isNaN(planned.getTime()) ? undefined : planned);
      setIsPostCreatorOpen(true);
    }
  };

  const handleClosePostCreator = () => {
    setIsPostCreatorOpen(false);
    setEditingPostId(undefined);
    setShootingMappingId(undefined);
    setShootingPlannedDate(undefined);
  };

  const handleCloseVariationCreator = () => {
    setIsVariationCreatorOpen(false);
    setShootingVariationMappingId(undefined);
  };

  return (
    <div>
      {/* Period Filter - tighter spacing */}
      <div className="mb-3 flex justify-end">
        <PeriodPills value={period} onChange={setPeriod} count={data?.items.length} />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex gap-3 overflow-x-hidden">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && data?.items.length === 0 && <EmptyState />}

      {/* Carousel */}
      {!isLoading && data && data.items.length > 0 && (
        <HorizontalCardCarousel>
          {data.items.map((item) => (
            <PostCard key={item.id} item={item} onCommit={handleCommit} />
          ))}
        </HorizontalCardCarousel>
      )}

      {/* View all link - tighter */}
      <div className="mt-3 text-center">
        <button
          onClick={handleViewAll}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {t('dashboard_mapped_view_all', { defaultValue: 'View all mapped posts →' })}
        </button>
      </div>

      {/* PostCreator Modal - for committing mapped posts */}
      <PostCreator
        isOpen={isPostCreatorOpen}
        onClose={handleClosePostCreator}
        editingPostId={editingPostId}
        shootingMappingId={shootingMappingId}
        initialScheduledDate={shootingPlannedDate}
      />

      {/* VariationCreator Modal - for committing mapped variations */}
      <VariationCreator
        isOpen={isVariationCreatorOpen}
        onClose={handleCloseVariationCreator}
        shootingMappingId={shootingVariationMappingId}
      />
    </div>
  );
};
